import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Field, Pill, Spinner, TextArea } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { useAuth } from "../lib/auth";
import { openQuotePDF, shareQuotePDF, shareBlob, downloadBlob, whatsappMessage, quoteDocumentLabel } from "../lib/pdf";
import { Eye, Share2, MessageCircle, Mail, Copy, Trash2 } from "lucide-react";
import { SamplingItemsEditor, newSamplingItem } from "../components/SamplingItemsEditor";
import { ProductionItemsEditor, newProductionStyle } from "../components/ProductionItemsEditor";
import { MaterialsEditor } from "../components/MaterialsEditor";
import { compressDataUrl } from "../lib/image";

const STATUSES = ["draft", "sent", "approved", "paid", "rejected"];

const SCHEMA = {
  initial_meeting: [
    { label: "Studio Grape representative", key: "studio_rep" },
    { label: "Category", key: "category" },
    { label: "Sampling price from (₹)", key: "sampling_price_from", numeric: true },
    { label: "Sampling price to (₹, optional)", key: "sampling_price_to", numeric: true },
    { label: "Base pattern making charges (₹)", key: "pattern_charges", numeric: true },
    { label: "Pattern grading charges (₹)", key: "grading_charges", numeric: true },
    { label: "Tentative sampling commencement", key: "sampling_start" },
    { label: "MOQs discussed", key: "moq_details", multiline: true },
    { label: "Production price (₹ / pc)", key: "production_price", numeric: true },
    { label: "Notes", key: "notes", multiline: true },
  ],
  sampling: [
    { label: "Title", key: "title" },
    { label: "GST %", key: "gst_rate", numeric: true },
    { label: "Special instructions", key: "special_instructions", multiline: true },
    { label: "Notes", key: "notes", multiline: true },
  ],
  production: [
    { label: "Title", key: "title" },
    { label: "GST %", key: "gst_rate", numeric: true },
    { label: "Notes", key: "notes", multiline: true },
  ],
};

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [edits, setEdits] = useState({});
  const [samplingItems, setSamplingItems] = useState([]);
  const [samplingMaterials, setSamplingMaterials] = useState([]);
  const [prodItems, setProdItems] = useState([]);
  const [prodMaterials, setProdMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  // When the browser drops the user-gesture context during PDF generation,
  // we can't call navigator.share() directly. Instead we stash the ready
  // bundle here and render a follow-up "Tap to share" button that carries a
  // fresh user gesture.
  const [readyBundle, setReadyBundle] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/quotes/${id}`);
      setQ(r);
      const ed = {};
      Object.entries(r.data || {}).forEach(([k, v]) => {
        if (k !== "items" && k !== "materials") ed[k] = String(v ?? "");
      });
      setEdits(ed);
      if (r.quote_type === "sampling") {
        const its = Array.isArray(r.data?.items) ? r.data.items : [];
        setSamplingItems(its.length ? its.map((it) => ({
          id: Math.random().toString(36).slice(2, 10),
          image: it.image || null, product_name: String(it.product_name ?? ""),
          num_samples: String(it.num_samples ?? "1"),
          pattern_base: String(it.pattern_base ?? ""), pattern_grading: String(it.pattern_grading ?? ""),
          grading_sizes: String(it.grading_sizes ?? ""),
          sampling_charges: String(it.sampling_charges ?? ""),
        })) : [newSamplingItem()]);
        const mats = Array.isArray(r.data?.materials) ? r.data.materials : [];
        setSamplingMaterials(mats.map((m) => ({
          id: Math.random().toString(36).slice(2, 10),
          name: String(m.name ?? ""), quantity: String(m.quantity ?? ""), price: String(m.price ?? ""),
        })));
      }
      if (r.quote_type === "production") {
        const its = Array.isArray(r.data?.items) ? r.data.items : [];
        setProdItems(its.length ? its.map((it) => ({
          id: Math.random().toString(36).slice(2, 10),
          image: it.image || null, product_name: String(it.product_name ?? ""),
          style_no: String(it.style_no ?? ""), num_pieces: String(it.num_pieces ?? "1"),
          pattern_base: String(it.pattern_base ?? ""), pattern_grading: String(it.pattern_grading ?? ""),
          grading_sizes: String(it.grading_sizes ?? ""),
          pricing_type: it.pricing_type === "jobwork" ? "jobwork" : "fob",
          production_price: String(it.production_price ?? ""),
        })) : [newProductionStyle()]);
        const mats = Array.isArray(r.data?.materials) ? r.data.materials : [];
        setProdMaterials(mats.map((m) => ({
          id: Math.random().toString(36).slice(2, 10),
          name: String(m.name ?? ""), quantity: String(m.quantity ?? ""), price: String(m.price ?? ""),
        })));
      }
    } catch (e) { alert(e?.message); navigate(-1); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...edits };
      if (q.quote_type === "sampling") {
        // Compress any oversized images so the quote doc stays well under
        // MongoDB's 16MB BSON limit (legacy quotes may carry full-res photos).
        const items = await Promise.all(samplingItems.map(async (it) => ({
          image: it.image ? await compressDataUrl(it.image) : null,
          product_name: it.product_name || "",
          num_samples: it.num_samples || "1",
          pattern_base: it.pattern_base || "0",
          pattern_grading: it.pattern_grading || "0",
          grading_sizes: it.grading_sizes || "0",
          sampling_charges: it.sampling_charges || "0",
        })));
        payload.items = items;
        payload.materials = samplingMaterials.map((m) => ({
          name: m.name || "", quantity: m.quantity || "0", price: m.price || "0",
        }));
      }
      if (q.quote_type === "production") {
        const items = await Promise.all(prodItems.map(async (it) => ({
          image: it.image ? await compressDataUrl(it.image) : null,
          product_name: it.product_name || "", style_no: it.style_no || "",
          num_pieces: it.num_pieces || "1", pattern_base: it.pattern_base || "0",
          pattern_grading: it.pattern_grading || "0", grading_sizes: it.grading_sizes || "0",
          pricing_type: it.pricing_type || "fob",
          production_price: it.production_price || "0",
        })));
        payload.items = items;
        payload.materials = prodMaterials.map((m) => ({ name: m.name || "", quantity: m.quantity || "0", price: m.price || "0" }));
      }
      const r = await api.patch(`/quotes/${id}`, { data: payload });
      setQ(r);
      alert("Saved");
    } catch (e) { alert(e?.message); } finally { setSaving(false); }
  };

  const setStatus = async (status) => setQ(await api.patch(`/quotes/${id}`, { status }));

  const onSharePDF = async () => {
    if (sharing) return;
    setReadyBundle(null);
    setSharing(true);
    try {
      const res = await shareQuotePDF(q);
      if (res.gestureLost && res.bundle) {
        // Browser blocked the share because our async PDF generation exceeded
        // the user-gesture window. Stash the bundle so a follow-up tap can
        // trigger navigator.share() with a fresh gesture.
        setReadyBundle(res.bundle);
      } else if (!res.shared && !res.cancelled) {
        alert("PDF downloaded. You can now attach it in WhatsApp / Email manually.");
      }
    } catch (e) {
      alert(`Could not generate PDF: ${e?.message || "Unknown error"}`);
    } finally {
      setSharing(false);
    }
  };

  const shareReady = async () => {
    if (!readyBundle) return;
    const bundle = readyBundle;
    try {
      const res = await shareBlob(bundle);
      if (res.shared) {
        setReadyBundle(null);
      } else {
        downloadBlob(bundle);
        setReadyBundle(null);
      }
    } catch (e) {
      if (e?.name === "AbortError") return; // user cancelled share sheet
      // Second attempt failed too — fall back to a plain download.
      downloadBlob(bundle);
      setReadyBundle(null);
    }
  };

  const onEmailPDF = async () => {
    if (sharing) return;
    setReadyBundle(null);
    setSharing(true);
    try {
      // Try Web Share first (which on mobile shows Email apps too with file attachment)
      const res = await shareQuotePDF(q);
      if (res.gestureLost && res.bundle) {
        setReadyBundle(res.bundle);
      } else if (!res.shared && !res.cancelled) {
        // Fallback: open mailto with the prefilled body so user can attach the just-downloaded PDF
        const subject = `Studio Grape · ${q.quote_number} · ${quoteDocumentLabel(q)}`;
        const body = whatsappMessage(q);
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
    } catch (e) {
      alert(`Could not generate PDF: ${e?.message || "Unknown error"}`);
    } finally {
      setSharing(false);
    }
  };
  const duplicate = async () => {
    const r = await api.post(`/quotes/${id}/duplicate`);
    navigate(`/quotes/${r.quote_id}`);
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try { await api.delete(`/quotes/${id}`); navigate("/quotes"); }
    catch (e) { alert(e?.message); }
  };

  if (loading || !q) return <><AppHeader title="Quote" back /><div className="sg-main"><Spinner /></div></>;

  const tone = q.status === "paid" || q.status === "approved" ? "success" : q.status === "rejected" ? "danger" : q.status === "sent" ? "brand" : "neutral";
  const locked = q.status === "approved";
  const fields = SCHEMA[q.quote_type] || [];

  return (
    <>
      <AppHeader title={q.quote_number} back right={<Pill label={q.status} tone={tone} />} />
      <div className="sg-main sg-fade-in">
        <div className="sg-card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: "var(--muted)", letterSpacing: 1, fontWeight: 800 }}>{String(q.quote_type).replace("_", " ").toUpperCase()}</div>
          <div style={{ fontSize: 19, fontWeight: 800, marginTop: 4 }}>{q.customer_name}</div>
          <div style={{ fontSize: 28, color: "var(--brand)", fontWeight: 800, marginTop: 12, letterSpacing: -0.5 }}>
            ₹{Number(q.amount).toLocaleString("en-IN")}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <button className="sg-btn" onClick={() => openQuotePDF(q)} data-testid="quote-preview" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <Eye size={16} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Preview PDF</span>
          </button>
          <button className="sg-btn sg-btn-primary" onClick={onSharePDF} disabled={sharing} data-testid="quote-share-pdf" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <Share2 size={16} />
            <span style={{ fontSize: 11, fontWeight: 700 }}>{sharing ? "Preparing..." : "Share PDF"}</span>
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          <button className="sg-btn" onClick={onSharePDF} disabled={sharing} data-testid="quote-whatsapp" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <MessageCircle size={16} color="#25D366" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>WhatsApp</span>
          </button>
          <button className="sg-btn" onClick={onEmailPDF} disabled={sharing} data-testid="quote-email" style={{ flexDirection: "column", padding: "12px 4px", gap: 4 }}>
            <Mail size={16} color="var(--brand)" />
            <span style={{ fontSize: 11, fontWeight: 700 }}>Email</span>
          </button>
        </div>

        {readyBundle && (
          <div style={{ background: "var(--surface-subtle)", border: "1px solid var(--brand)", borderRadius: 12, padding: 12, marginBottom: 14, display: "flex", flexDirection: "column", gap: 10 }} data-testid="quote-ready-share-panel">
            <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.4 }}>
              <b>PDF is ready.</b> Your browser paused the share sheet while the PDF was generating — tap below to open it now.
            </div>
            <button className="sg-btn sg-btn-primary" onClick={shareReady} data-testid="quote-share-ready" style={{ width: "100%" }}>
              <Share2 size={16} /> Tap to share PDF now
            </button>
            <button className="sg-btn" onClick={() => { downloadBlob(readyBundle); setReadyBundle(null); }} data-testid="quote-download-ready" style={{ width: "100%" }}>
              Or download the PDF
            </button>
          </div>
        )}

        <div className="sg-section-h">Status</div>
        <div className="sg-chip-row" style={{ marginBottom: 16 }}>
          {STATUSES.map((st) => (
            <button key={st} className={`sg-chip ${q.status === st ? "sg-chip-active" : ""}`} onClick={() => setStatus(st)} data-testid={`set-quote-status-${st}`}>{st}</button>
          ))}
        </div>

        <div className="sg-section-h">Editable fields</div>
        {fields.map((f) => (
          f.multiline ? (
            <TextArea key={f.key} label={f.label} value={edits[f.key] || ""} onChange={(e) => setEdits((d) => ({ ...d, [f.key]: e.target.value }))} data-testid={`edit-${f.key}`} disabled={locked} />
          ) : (
            <Field key={f.key} label={f.label} value={edits[f.key] || ""} onChange={(e) => setEdits((d) => ({ ...d, [f.key]: e.target.value }))} inputMode={f.numeric ? "numeric" : undefined} data-testid={`edit-${f.key}`} disabled={locked} />
          )
        ))}

        {q.quote_type === "sampling" && !locked && (
          <>
            <SamplingItemsEditor
              items={samplingItems}
              materials={samplingMaterials}
              onChange={setSamplingItems}
              gstRate={Number(edits.gst_rate || 5)}
            />
            <MaterialsEditor materials={samplingMaterials} onChange={setSamplingMaterials} sectionTitle="Materials (optional)" />
          </>
        )}
        {q.quote_type === "production" && !locked && (
          <ProductionItemsEditor items={prodItems} materials={prodMaterials} onItemsChange={setProdItems} onMaterialsChange={setProdMaterials} gstRate={Number(edits.gst_rate || 5)} />
        )}

        {!locked && (
          <button className="sg-btn sg-btn-primary" onClick={save} disabled={saving} data-testid="quote-save" style={{ width: "100%", marginTop: 4 }}>
            {saving ? "Saving..." : "Save changes"}
          </button>
        )}
        {locked && (
          <div className="sg-card" style={{ background: "var(--surface-subtle)", color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
            Approved quotes are locked. Change status to draft to edit.
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: user?.role === "admin" ? "1fr 1fr" : "1fr", gap: 8, marginTop: 12 }}>
          <button className="sg-btn" onClick={duplicate} data-testid="quote-duplicate"><Copy size={14} /> Duplicate</button>
          {user?.role === "admin" && (
            <button className="sg-btn sg-btn-danger" onClick={onDelete} data-testid="quote-delete"><Trash2 size={14} /> Delete</button>
          )}
        </div>
      </div>
    </>
  );
}
